import React, { forwardRef, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { groupBy } from 'lodash';

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import {
  BeautifulMentionsPlugin,
  BeautifulMentionNode,
  useBeautifulMentions
} from 'lexical-beautiful-mentions';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

import {
  lexicalBeautifulMentionsTag,
  termTranslationShape,
  sourceShape
} from '../../../propsShape';
import {
  ColumnMentionNode,
  ColumnMentionsDropdownItem,
  ColumnMentionsDropdownMenu,
  TermTranslationNode,
  MetricMentionNode,
  MetricMentionsDropdownMenu,
  MetricMentionsDropdownItem
} from '../../molecules';
import { EditorOnReadyPlugin } from '../EditorOnReadyPlugin';
import { TermHighlightPlugin } from '../TermHighlightPlugin';
import { NodeReferencePlugin } from '../NodeReferencePlugin';
import { MatchingEntityHighlightPlugin } from '../MatchingEntityHighlightPlugin';
import { MatchingEntityMentionNode } from '../../molecules/MatchingEntityMentionNode';
import { DirectKeyPressPlugin } from '../DirectKeyPressPlugin';

const BasePromptContainer = styled.div`
  position: relative;
  background: transparent;
  border: none;

  padding: 0;
  font-size: 13px;
  font-family: var(--font-family-body);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.02em;

  color: var(--content-body);
  caret-color: var(--primary-default);

  transition: background 500ms linear;
`;

const Placeholder = styled.div`
  position: absolute;
  left: 12px;
  top: 14px;
  pointer-events: none;

  font-family: var(--font-family-body);
  color: var(--content-subtitle);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.02em;
  transition: opacity 0.5s 0.5s ease, text-indent 0.5s 0.25s ease;

  opacity: 1;
  text-indent: 0;

  ${BasePromptContainer}:focus-within > & {
    outline: none;

    &::placeholder {
      opacity: 0;
      text-indent: -100%;
    }
  }
`;

const StyledContentEditable = styled(ContentEditable)`
  padding: 12px 12px 60px 12px;

  min-height: 40px;
  box-sizing: border-box;
  position: relative;
  width: 100%;

  border-radius: 10px;
  border-width: 1px;
  border-style: solid;
  column-gap: 8px;
  cursor: text;
  outline: none;
  box-shadow: 0 4px 6px 0 var(--border-light);

  transition: background-color 0.1s 0.05s ease, box-shadow 0.1s 0s ease, border-color 0.1s ease;

  background-color: var(--elevation-high);
  border-color: var(--border-muted);

  &:focus-within {
    border-color: var(--border-default);
  }

  p {
    margin: 0;
    padding-right: 24px;
    line-height: 24px;
  }

  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    background-color: var(--disabled-background);
    border-color: var(--border-muted);
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
`;

const PUNCTUATION = '\\.,\\*\\?\\$\\|#{}\\(\\)\\^\\[\\]\\\\/!%\'"~=<>:;';

const InsertMentionsPlugin = ({ insertMentionRef }) => {
  const { insertMention } = useBeautifulMentions();

  useEffect(() => {
    if (insertMentionRef) {
      // eslint-disable-next-line no-param-reassign
      insertMentionRef.current = insertMention;
    }
  }, [insertMention, insertMentionRef]);

  return null;
};

InsertMentionsPlugin.propTypes = {
  insertMentionRef: PropTypes.shape({
    current: PropTypes.func
  })
};

const BasePrompt = forwardRef(
  (
    {
      mentionItems,
      sources,
      placeholder = 'Ask anything...',
      className = null,
      inputClassName = null,
      renderSubmitButton = null,
      terms = [],
      onKeyDown = null,
      onChange = null,
      disabled = false,
      actions = null,
      insertMentionRef = null,
      findEntityMatches = null,
      handleSelectEntity = null,
      matchingEntities = []
    },
    editorRef
  ) => {
    const [mentionActive, setMentionActive] = useState(false);
    const ref = useRef(null);
    const [displayNestedMenu, setDisplayNestedMenu] = useState(false);

    const setNodeRef = (element) => {
      ref.current = element;
    };

    const renderColumnMenu = useCallback(
      (props) => (
        <ColumnMentionsDropdownMenu
          {...props}
          hideHeader={displayNestedMenu}
          referenceElement={ref.current}
        />
      ),
      [ref, displayNestedMenu]
    );

    const renderMetricMenu = useCallback(
      (props) => <MetricMentionsDropdownMenu {...props} referenceElement={ref.current} />,
      [ref]
    );

    const editorConfig = {
      // eslint-disable-next-line no-console
      onError: (error) => console.log(error),
      nodes: [
        ColumnMentionNode,
        MetricMentionNode,
        TermTranslationNode,
        MatchingEntityMentionNode,
        {
          replace: BeautifulMentionNode,
          with: (node) => {
            const trigger = node.getTrigger();

            if (trigger === '@') {
              return new ColumnMentionNode(node.getTrigger(), node.getValue(), node.getData());
            }
            if (trigger === '#') {
              return new MetricMentionNode(node.getTrigger(), node.getValue(), node.getData());
            }
            return new BeautifulMentionNode(node.getTrigger(), node.getValue(), node.getData());
          }
        }
      ]
    };

    const groupedColumns = useMemo(() => {
      const items = Object.entries(groupBy(mentionItems.columns, 'schema')).map(
        ([schema, views]) => ({
          value: schema,
          source: mentionItems.columns.find(({ schema: _schema }) => _schema === schema).source,
          views: Object.entries(groupBy(views, 'view')).map(([view, columns]) => ({
            value: view,
            description: columns.find(({ view: _view }) => _view === view).viewDescription,
            columns
          }))
        })
      );
      return items;
    }, [mentionItems.columns]);

    const groupColumns = (columnsArray) =>
      Object.entries(groupBy(columnsArray, 'schema')).map(([schema, items]) => ({
        value: schema,
        source: items[0].source, // assumes all items for a schema share the same source
        views: Object.entries(groupBy(items, 'view')).map(([view, columns]) => ({
          value: view,
          description: columns[0].viewDescription, // assumes uniform view description
          columns
        }))
      }));

    const handleNativeKeyDown = useCallback(
      (event) => {
        if (onKeyDown) {
          onKeyDown(event);
        }
      },
      [onKeyDown]
    );

    return (
      <LexicalComposer initialConfig={editorConfig}>
        <BasePromptContainer className={className} ref={ref}>
          <PlainTextPlugin
            contentEditable={
              <StyledContentEditable
                className={inputClassName}
                onKeyDown={handleNativeKeyDown}
                disabled={disabled}
              />
            }
            placeholder={<Placeholder>{placeholder}</Placeholder>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <BeautifulMentionsPlugin
            items={{ '@': [] }}
            insertOnBlur={false}
            menuComponent={renderColumnMenu}
            menuItemComponent={ColumnMentionsDropdownItem}
            menuItemLimit={10}
            punctuation={PUNCTUATION}
            onMenuOpen={() => setMentionActive(true)}
            onMenuClose={() => setMentionActive(false)}
            searchDelay={0}
            onSearch={async (trigger, query) => {
              if (query.length === 0) {
                setDisplayNestedMenu(true);
                return groupedColumns;
              }

              let filteredColumns = [];

              // Filter the columns based on the query.
              const lowerQuery = query.toLowerCase();
              if (lowerQuery.includes('.')) {
                const [viewQuery, columnQuery] = lowerQuery.split('.');
                filteredColumns = mentionItems.columns.filter(
                  ({ view, column }) =>
                    view.toLowerCase().startsWith(viewQuery) &&
                    (!columnQuery || column.toLowerCase().startsWith(columnQuery))
                );
              } else {
                filteredColumns = mentionItems.columns.filter(({ column }) =>
                  column.toLowerCase().includes(lowerQuery)
                );
              }
              // If there are more than 10 matches, return the grouped (nested) view.
              if (filteredColumns.length > 10) {
                return groupColumns(filteredColumns);
              }

              // Otherwise, disable the nested menu and return a flat list of matches.
              setDisplayNestedMenu(false);
              return filteredColumns;
            }}
          />
          <BeautifulMentionsPlugin
            items={{ '#': mentionItems.metrics }}
            menuComponent={renderMetricMenu}
            menuItemComponent={MetricMentionsDropdownItem}
            punctuation={PUNCTUATION}
            onMenuOpen={() => setMentionActive(true)}
            onMenuClose={() => setMentionActive(false)}
          />
          <AutoFocusPlugin />
          {!mentionActive && <TermHighlightPlugin terms={terms} />}
          {!mentionActive && handleSelectEntity && (
            <MatchingEntityHighlightPlugin
              entities={matchingEntities}
              onSelectEntity={handleSelectEntity}
              sources={sources}
              terms={terms}
            />
          )}
          <EditorOnReadyPlugin
            onReady={(editor) => {
              // eslint-disable-next-line no-param-reassign
              editorRef.current = editor;
            }}
          />
          {insertMentionRef && <InsertMentionsPlugin insertMentionRef={insertMentionRef} />}
          <NodeReferencePlugin onNodeRef={setNodeRef} />
          <DirectKeyPressPlugin findEntityMatches={findEntityMatches} />
          <OnChangePlugin onChange={onChange} />
          <ActionsContainer>
            {actions && actions}
            {renderSubmitButton && renderSubmitButton()}
          </ActionsContainer>
        </BasePromptContainer>
      </LexicalComposer>
    );
  }
);

BasePrompt.propTypes = {
  placeholder: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  renderSubmitButton: PropTypes.func,
  mentionItems: PropTypes.shape({
    columns: PropTypes.arrayOf(lexicalBeautifulMentionsTag),
    metrics: PropTypes.arrayOf(lexicalBeautifulMentionsTag)
  }).isRequired,
  terms: PropTypes.arrayOf(termTranslationShape),
  onKeyDown: PropTypes.func,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  actions: PropTypes.node,
  insertMentionRef: PropTypes.shape({
    current: PropTypes.func
  }),
  handleSelectEntity: PropTypes.func,
  findEntityMatches: PropTypes.func,
  sources: PropTypes.arrayOf(sourceShape),
  matchingEntities: PropTypes.arrayOf(
    PropTypes.shape({
      entity: PropTypes.string.isRequired,
      suggestions: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          column: PropTypes.string,
          schema: PropTypes.string,
          table: PropTypes.string,
          score: PropTypes.number
        })
      )
    })
  )
};

export { BasePrompt };
